import {
  ArrowRightIcon,
  CheckCircleIcon,
  InfoIcon,
  SlidersHorizontalIcon,
} from '@phosphor-icons/react';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  hasAccents,
  questionTopicKey,
  usePatchCriteria,
  type CriteriaState,
  type ScoreComponent,
} from '@entities/search-session';
import { Button, ProgressBar } from '@shared/ui';

import { criteriaWarningKey } from '../model/warnings';

import styles from './Criteria.module.css';
import { CriteriaRowList } from './CriteriaRowList';
import { HardCriteriaBlock } from './HardCriteriaBlock';
import { readinessPercent } from './readiness';
import { PANEL_ROW_ORDER, useCriteriaRows } from './useCriteriaRows';
import { WeightsBlock } from './WeightsBlock';

interface CriteriaPanelProps {
  sessionId: string;
  criteria: CriteriaState;
  /** Подтверждение критериев: дальше идёт запуск подбора. */
  onConfirm: () => void;
  /** Запуск подбора уже отправлен: повторное нажатие ничего не добавит. */
  confirmPending?: boolean | undefined;
  /** Готовый текст ошибки запуска. Ключи разбирает страница, а не панель. */
  confirmError?: string | undefined;
  /** Действие в шапке: на телефоне — закрытие шторки. */
  headerSlot?: ReactNode | undefined;
}

/**
 * Панель «Ваши критерии» (FE-071, MVP_PRD §9).
 *
 * Обновляется после каждого ответа: состояние приходит вместе с репликой
 * консультанта, отдельного запроса за критериями нет.
 *
 * Готовность — число backend'а (`readiness_score`), а не подсчёт заполненных
 * полей на клиенте: поиск разрешён ровно при 100, и второй источник правды
 * здесь означал бы кнопку, которая обещает недоступное.
 */
export function CriteriaPanel({
  sessionId,
  criteria,
  onConfirm,
  confirmPending = false,
  confirmError,
  headerSlot,
}: CriteriaPanelProps) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const [resetting, setResetting] = useState<ScoreComponent | null>(null);
  const [droppingMake, setDroppingMake] = useState(false);
  const [droppingModel, setDroppingModel] = useState(false);

  /**
   * Тема вопроса. Незнакомый код показывается общей формулировкой, а не самим
   * кодом: `WHEEL_SIZE_VS_POWER` в интерфейсе — это утечка внутренностей.
   */
  const questionTopic = (code: string) => {
    const key = questionTopicKey(code);
    return i18n.exists(key) ? t(key) : t('criteria.readiness.unknownTopic');
  };

  const patch = usePatchCriteria(sessionId);
  const rows = useCriteriaRows(criteria.payload, criteria.demotions);
  const ready = criteria.ready_for_confirmation;
  const readiness = readinessPercent(criteria);

  const warnings = criteria.payload.warnings
    .map((warning) => criteriaWarningKey(warning.code))
    .filter((key): key is string => key !== null);
  const uniqueWarnings = [...new Set(warnings)];

  const resetEmphasis = (component: ScoreComponent) => {
    setResetting(component);
    patch.mutate(
      {
        criteriaVersionId: criteria.version_id,
        operations: [{ path: `emphasis.${component}`, value: 'normal' }],
      },
      {
        onSettled: () => {
          setResetting(null);
        },
      },
    );
  };

  /**
   * Снять обязательность марки одним действием (FE-390).
   *
   * Пустой список — это **снятие ограничения**, и цитаты оно не требует
   * (MATCHING §3.2). Назначить обязательность отсюда нельзя: жёсткость
   * приходит ответом на вопрос Karl, а не переключателем на панели.
   */
  const dropMakeRequirement = () => {
    setDroppingMake(true);
    patch.mutate(
      {
        criteriaVersionId: criteria.version_id,
        operations: [{ path: 'makes.required', value: [] }],
      },
      {
        onSettled: () => {
          setDroppingMake(false);
        },
      },
    );
  };

  /**
   * Снять обязательность модели (FE-530, KARL_DIALOG_RFC §8.1).
   *
   * Модель не выбрасывается, а становится предпочтением: «только Golf» → «Golf
   * и похожие». Это ровно то же смягчение, что предлагает воронка при нулевой
   * выдаче, и терять названную модель здесь незачем. Порядок операций важен:
   * непустой `required` очищает `preferred`, поэтому обязательность снимается
   * первой.
   */
  const dropModelRequirement = () => {
    setDroppingModel(true);
    patch.mutate(
      {
        criteriaVersionId: criteria.version_id,
        operations: [
          { path: 'models.required', value: [] },
          { path: 'models.preferred', value: criteria.payload.models.required },
        ],
      },
      {
        onSettled: () => {
          setDroppingModel(false);
        },
      },
    );
  };

  return (
    <section className={styles.panel} aria-labelledby={titleId}>
      <header className={styles.panelHead}>
        <div className={styles.panelHeadMain}>
          <SlidersHorizontalIcon size={18} weight="bold" aria-hidden="true" />
          <h2 id={titleId} className={styles.panelTitle}>
            {t('criteria.panel.title')}
          </h2>
        </div>
        {headerSlot}
      </header>

      <div className={styles.readiness}>
        {ready ? (
          <p className={styles.readinessReady}>
            <CheckCircleIcon size={16} weight="fill" aria-hidden="true" />
            {t('criteria.readiness.ready')}
          </p>
        ) : (
          <p className={styles.readinessLabel}>{t('criteria.readiness.label')}</p>
        )}
        {/* Полоса полноты — синяя всегда, и при 100 % тоже: синий значит
            «прогресс», а зелёный — «соответствует», и готовность запроса
            утверждением о данных не является (DESIGN_SYSTEM §2.3–§2.4). */}
        <ProgressBar percent={readiness} label={t('criteria.readiness.label')} showValue />
        {/* Готовность 100 % не означает, что вопросы кончились: география,
            оснащение и пороги на неё не влияют (MATCHING §3.4). Поэтому при
            готовности оставшийся вопрос подписан как необязательное уточнение,
            а не как «осталось». */}
        {criteria.next_question ? (
          <p className={styles.readinessNext}>
            {t(ready ? 'criteria.readiness.optional' : 'criteria.readiness.next', {
              question: questionTopic(criteria.next_question.question_code),
            })}
          </p>
        ) : null}
      </div>

      <div className={styles.panelBody}>
        <CriteriaRowList rows={rows} order={PANEL_ROW_ORDER} />

        {/* Обязательные условия — единым списком: именно из них складывается
            пустая выдача, и разбросанными по разделам они читаются как
            пожелания (FE-390, MVP_PRD §10). */}
        <HardCriteriaBlock
          payload={criteria.payload}
          disabled={patch.isPending}
          dropping={droppingMake}
          droppingModel={droppingModel}
          onDropMakeRequirement={dropMakeRequirement}
          onDropModelRequirement={dropModelRequirement}
        />

        {hasAccents(criteria.weights) ? (
          <WeightsBlock
            weights={criteria.weights}
            disabled={patch.isPending}
            resetting={resetting}
            onReset={resetEmphasis}
          />
        ) : null}

        {uniqueWarnings.map((key) => (
          <p key={key} className={styles.note} role="note">
            <InfoIcon size={15} weight="fill" aria-hidden="true" />
            {t(key)}
          </p>
        ))}

        {patch.isError ? (
          <p className={styles.error} role="alert">
            {t('criteria.errors.patchFailed')}
          </p>
        ) : null}
      </div>

      <footer className={styles.panelFoot}>
        {confirmError !== undefined ? (
          <p className={styles.error} role="alert">
            {confirmError}
          </p>
        ) : null}

        {/* Подбор разрешён строго при `readiness_score = 100`: кнопка не
            обещает того, что backend отклонит. */}
        <Button
          // Пока критериев не хватает, кнопка ещё и нейтральная: выключенный
          // брендовый CTA всё равно читается как призыв нажать.
          variant={ready ? 'primary' : 'secondary'}
          fullWidth
          disabled={!ready || confirmPending}
          onClick={onConfirm}
          iconEnd={<ArrowRightIcon size={16} weight="bold" />}
        >
          {confirmPending ? t('criteria.panel.confirming') : t('criteria.panel.confirm')}
        </Button>
      </footer>
    </section>
  );
}
